
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Attachment, AttachmentCard } from "./AttachmentCard";
import { AttachmentUploader } from "./AttachmentUploader";
import { toast } from "@/components/ui/sonner";

interface AttachmentsSectionProps {
  entryId?: string;
  userId?: string;
  editable?: boolean;
  isCreator?: boolean;
}

export const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({
  entryId,
  userId,
  editable = false,
  isCreator = false,
}) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAttachments = async () => {
    if (!entryId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("files")
      .select("*")
      .eq("entry_id", entryId)
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Failed to load attachments");
      setLoading(false);
      return;
    }
    setAttachments(data || []);
    setLoading(false);
  };

  const handleDelete = async (attachmentId: string) => {
    // Find the attachment for file path
    const attachment = attachments.find(a => a.id === attachmentId);
    if (!attachment) return;

    // Remove from storage
    const { error: storageError } = await supabase.storage
      .from("entry_attachments")
      .remove([attachment.storage_path]);
    if (storageError) {
      toast.error("Failed to delete file from storage.");
      return;
    }

    // Remove from DB
    const { error: dbError } = await supabase.from("files").delete().eq("id", attachmentId);
    if (dbError) {
      toast.error("Failed to delete attachment.");
      return;
    }

    toast.success("Attachment deleted.");
    // Refresh list
    fetchAttachments();
  };

  useEffect(() => {
    fetchAttachments();
    // eslint-disable-next-line
  }, [entryId]);

  if (!entryId) return null;

  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold mb-2">Attachments</h2>
      {editable && userId && (
        <AttachmentUploader entryId={entryId} userId={userId} onUpload={fetchAttachments} />
      )}
      {loading ? (
        <div className="text-sm text-muted-foreground py-4">Loading attachments...</div>
      ) : attachments.length === 0 ? (
        <div className="text-muted-foreground py-4 text-sm">No attachments yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {attachments.map(att => (
            <AttachmentCard
              key={att.id}
              attachment={att}
              onDelete={handleDelete}
              isCreator={isCreator}
            />
          ))}
        </div>
      )}
    </div>
  );
};
